import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { AccountInfo, InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import { tokenRequest } from '@/config/authConfig';
import { setTokenGetter } from '@/services/api';
import { setTokenGetterForClient } from '@/api/client';
import { featureFlags } from '@/config/featureFlags';
import api from '@/services/api';
import { initializePermissions, PERMISSIONS, type Permission } from '@/constants/permissions';

interface UserPermissions {
  email: string;
  name: string;
  role: string;
  employeeId?: number;
  permissions?: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  userRole: string | null;
  userPermissions: UserPermissions | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  isLoading: boolean;
  ssoAttempted: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const fetchAndInitializePermissions = async (): Promise<void> => {
  try {
    const response = await api.get<Permission[]>('/admin/permission-roles/permissions');
    initializePermissions(response.data);
  } catch (error) {
    console.error('Failed to fetch available permissions:', error);
  }
};

/**
 * AuthProvider with SSO (Azure AD MSAL)
 * Used when featureFlags.ssoEnabled = true
 */
const AuthProviderWithSSO: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const [ssoAttempted, setSsoAttempted] = useState(false);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [permissionsFetched, setPermissionsFetched] = useState(false);
  const [isReauthenticating, setIsReauthenticating] = useState(false);

  const fetchAvailablePermissions = useCallback(async () => {
    try {
      const response = await api.get<Permission[]>('/admin/permission-roles/permissions');
      initializePermissions(response.data);
      console.log('Available permissions loaded:', response.data.length);
    } catch (error) {
      console.error('Failed to fetch available permissions:', error);
    }
  }, []);

  // Fetch user role from backend
  const fetchUserPermissions = useCallback(async () => {
    if (permissionsFetched || isReauthenticating) return; // Prevent multiple calls

    setPermissionsFetched(true);
    try {
      const response = await api.get<UserPermissions>('/user/permissions');
      // Initialize available permissions BEFORE setting userPermissions so that
      // PERMISSIONS.* constants are already populated when the first re-render fires.
      await fetchAvailablePermissions();
      setUserPermissions(response.data);
      setUserRole(response.data.role);
      console.log('User role loaded:', response.data);
    } catch (error) {
      // DON'T redirect on 401 here - it could be a permissions issue, not expired token
      // Token expiration is handled by MSAL's InteractionRequiredAuthError
      const axiosError = error as { response?: { status?: number }; message?: string };
      console.error('Failed to fetch permissions:', axiosError?.response?.status, axiosError?.message);

      // FALLBACK: Set mock data when backend is not available
      // This handles both network errors and permission errors gracefully
      setUserPermissions({
        email: accounts[0]?.username || 'unknown',
        name: accounts[0]?.name || 'Unknown User',
        role: 'GUEST',
        permissions: [],
      });
      setUserRole('GUEST');
    }
  }, [permissionsFetched, accounts, isReauthenticating, fetchAvailablePermissions]);

  // Handle token expiration - force re-authentication
  const handleTokenExpired = useCallback(async () => {
    if (isReauthenticating) {
      console.log('Already re-authenticating, skipping...');
      return;
    }

    console.log('🔄 Token expired - forcing re-authentication');
    setIsReauthenticating(true);

    try {
      // Clear local React state
      setUserPermissions(null);
      setUserRole(null);
      setPermissionsFetched(false);

      // Use the official MSAL API to clear its cache instead of touching
      // localStorage/sessionStorage directly — manual removal desynchronizes
      // the in-memory state from the persisted state, causing state_mismatch errors.
      console.log('🧹 Clearing MSAL cache via official API...');
      try {
        await instance.clearCache();
      } catch {
        // clearCache may not exist in older builds; safe to continue without it
      }

      // Save current page so main.tsx can restore it after the OAuth redirect lands on /
      if (!sessionStorage.getItem('alloc.redirectStartPage')) {
        sessionStorage.setItem('alloc.redirectStartPage', window.location.href);
      }

      const clientId = import.meta.env.VITE_AZURE_CLIENT_ID || '';
      await instance.loginRedirect({
        scopes: [`api://${clientId}/Admin`, 'openid', 'profile', 'email'],
        prompt: 'login',
        // No redirectStartPage — see login() comment above.
      });
    } catch (error) {
      console.error('❌ Re-authentication failed:', error);
      setIsReauthenticating(false);
      window.location.href = '/';
    }
  }, [instance, isReauthenticating]);

  // Setup token getter for API services (both axios and fetch clients)
  useEffect(() => {
    const getToken = async () => {
      if (!isAuthenticated || accounts.length === 0 || isReauthenticating) {
        return null;
      }

      try {
        // ✅ FIX: Add timeout to prevent indefinite hanging
        const tokenPromise = instance.acquireTokenSilent({
          scopes: tokenRequest.scopes,
          account: accounts[0],
        });

        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Token acquisition timeout after 10s')), 10000)
        );

        const response = await Promise.race([tokenPromise, timeoutPromise]);
        return response.accessToken;
      } catch (error) {
        // Check if it's a timeout
        if (error instanceof Error && error.message.includes('timeout')) {
          console.error('⏱️ Token acquisition timed out - triggering re-authentication');
          await handleTokenExpired();
          return null;
        }

        // Check if it's an interaction required error (token expired)
        if (error instanceof InteractionRequiredAuthError) {
          console.error('❌ Token expired - interaction required:', error.errorCode);
          // Trigger re-authentication
          await handleTokenExpired();
          return null;
        }

        console.error('Token acquisition failed:', error);
        return null;
      }
    };

    // Register token getter for both API clients
    setTokenGetter(getToken); // For services/api.ts (axios)
    setTokenGetterForClient(getToken); // For api/client.ts (fetch)
  }, [instance, accounts, isAuthenticated, isReauthenticating, handleTokenExpired]);

  // Fetch user role when authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading && inProgress === InteractionStatus.None && !permissionsFetched && !isReauthenticating) {
      fetchUserPermissions();
    } else if (!isAuthenticated && !isReauthenticating) {
      setUserPermissions(null);
      setUserRole(null);
      setPermissionsFetched(false); // Reset when logged out
    }
  }, [isAuthenticated, isLoading, inProgress, permissionsFetched, isReauthenticating, fetchUserPermissions]);

  useEffect(() => {
    // Check authentication status without attempting silent SSO
    const checkAuthentication = async () => {
      // Don't check if already in progress or already checked
      if (inProgress !== InteractionStatus.None || ssoAttempted) {
        return;
      }

      setSsoAttempted(true);

      try {
        const currentAccounts = instance.getAllAccounts();
        console.log('📋 Accounts in cache:', currentAccounts.length);

        if (currentAccounts.length > 0) {
          setIsLoading(false);
          return;
        }

        // No account in cache - user needs to login
        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    };

    checkAuthentication();
  }, [instance, inProgress, ssoAttempted]);

  const login = async () => {
    try {
      const clientId = import.meta.env.VITE_AZURE_CLIENT_ID || '';
      await instance.loginRedirect({
        scopes: [`api://${clientId}/Admin`, 'openid', 'profile', 'email'],
        prompt: 'select_account',
        // No redirectStartPage — MSAL always returns to the root redirectUri.
        // Deep-link restoration is handled via alloc.redirectStartPage in sessionStorage
        // (written by ProtectedRoute, consumed by main.tsx after handleRedirectPromise).
      });
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Clear local state first
      setUserPermissions(null);
      setUserRole(null);
      setPermissionsFetched(false);
      setSsoAttempted(false);
      setIsLoading(false);

      // Get the account to logout
      const account = accounts.length > 0 ? accounts[0] : undefined;

      // Clear localStorage to prevent SSO from reusing old session
      localStorage.clear();
      sessionStorage.clear();

      // Perform logout redirect
      await instance.logoutRedirect({
        account: account,
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, clear local state
      setUserPermissions(null);
      setUserRole(null);
      setPermissionsFetched(false);
      setSsoAttempted(false);
      setIsLoading(false);
      localStorage.clear();
      sessionStorage.clear();
      throw error;
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    if (!isAuthenticated || accounts.length === 0 || isReauthenticating) {
      return null;
    }

    try {
      // ✅ FIX: Add timeout to prevent indefinite hanging
      const tokenPromise = instance.acquireTokenSilent({
        scopes: tokenRequest.scopes,
        account: accounts[0],
      });

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Token acquisition timeout after 10s')), 10000)
      );

      const response = await Promise.race([tokenPromise, timeoutPromise]);
      return response.accessToken;
    } catch (error) {
      console.error('Silent token acquisition failed:', error);

      // Check if it's a timeout
      if (error instanceof Error && error.message.includes('timeout')) {
        console.error('⏱️ Token acquisition timed out - triggering re-authentication');
        await handleTokenExpired();
        return null;
      }

      // Check if it's an interaction required error (token expired)
      if (error instanceof InteractionRequiredAuthError) {
        console.error('❌ Token expired - forcing re-authentication');
        // Trigger re-authentication
        await handleTokenExpired();
        return null;
      }

      // Other errors - don't redirect automatically to avoid infinite loops
      return null;
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    user: accounts[0] || null,
    userRole,
    userPermissions,
    login,
    logout,
    getAccessToken,
    isLoading,
    ssoAttempted,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * AuthProvider WITHOUT SSO (Development Mode)
 * Used when featureFlags.ssoEnabled = false
 *
 * Simulates authenticated state with anonymous user for development.
 * Aligns with backend's token-validation-enabled: false mode.
 */

// Full permission set granted in dev mode — mirrors what a FULL_ADMIN gets from the backend.
// AuthProviderWithoutSSO is only mounted when SSO is disabled (i.e. local dev), so this
// override never affects staging or production.
const DEV_FULL_ADMIN_PERMISSIONS: UserPermissions = {
  email: 'anonymous@local.dev',
  name: 'Anonymous User (Dev Mode)',
  role: 'FULL_ADMIN',
  permissions: [
    'CapacityManagement.FullAdmin.Execute',
    'CapacityManagement.Team.Read',
    'CapacityManagement.ValueStream.Read',
    'CapacityManagement.Product.Read',
    'CapacityManagement.Structure.Read',
    'CapacityManagement.Vertical.Read',
    'CapacityManagement.CapacityManagement.Read',
    'CapacityManagement.Schedule.Read',
    'CapacityManagement.People.ReadAll',
    'CapacityManagement.People.CompanyColumnGridRead',
    'CapacityManagement.People.CompanyFilterRead',
    'CapacityManagement.People.WriteAbiEmail',
    'CapacityManagement.People.WriteIsLineManager',
    'CapacityManagement.EditPersonCompany.Write',
    'CapacityManagement.EditPersonName.Write',
    'CapacityManagement.EditPersonID.Write',
    'CapacityManagement.PeopleFundedBy.Write',
    'CapacityManagement.PeopleFundedBy.Read',
    'CapacityManagement.PeopleSeniority.Write',
    'CapacityManagement.PeopleSeniority.Read',
    'CapacityManagement.PeopleLocation.Write',
    'CapacityManagement.PeopleLocation.Read',
    'CapacityManagement.PeopleInvestment.Write',
    'CapacityManagement.PeopleInvestment.Read',
    'CapacityManagement.RevampUI.Read',
  ],
};

const AuthProviderWithoutSSO: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  const mockUser: AccountInfo = {
    homeAccountId: 'dev-mode',
    environment: 'local',
    tenantId: 'local-dev',
    username: 'anonymous@local.dev',
    localAccountId: 'anonymous-local',
    name: 'Anonymous User (Dev Mode)',
  };

  useEffect(() => {
    const noToken = async () => null;
    setTokenGetter(noToken);
    setTokenGetterForClient(noToken);
  }, []);

  // Fetch user permissions from backend then override to FULL_ADMIN for dev.
  // The backend user may not have admin permissions configured locally; this
  // ensures dev work is never blocked by missing role assignments.
  useEffect(() => {
    const fetchUserPermissions = async () => {
      try {
        const response = await api.get<UserPermissions>('/user/permissions');
        // Initialize available permissions BEFORE setting userPermissions so that
        // PERMISSIONS.* constants are already populated when the first re-render fires.
        await fetchAndInitializePermissions();
        console.log('User role loaded (dev override → FULL_ADMIN):', response.data);
      } catch {
        console.warn('⚠️ Dev Mode: /user/permissions unreachable — falling back to FULL_ADMIN');
      } finally {
        // Always apply FULL_ADMIN in dev mode regardless of what the backend returned.
        setUserPermissions(DEV_FULL_ADMIN_PERMISSIONS);
        setUserRole('FULL_ADMIN');
        setIsLoading(false);
      }
    };

    fetchUserPermissions();
  }, []);

  // No-op functions for dev mode
  const login = async () => {
    // No-op in dev mode
  };

  const logout = async () => {
    // No-op in dev mode
  };

  const getAccessToken = async (): Promise<string | null> => {
    return null; // No token in dev mode
  };

  const value: AuthContextType = {
    isAuthenticated: true, // Always authenticated in dev mode
    user: mockUser,
    userRole,
    userPermissions,
    login,
    logout,
    getAccessToken,
    isLoading,
    ssoAttempted: true, // Mark as attempted to prevent loading states
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Main AuthProvider that switches between SSO and non-SSO modes
 * based on feature flag
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (featureFlags.ssoEnabled) {
    return <AuthProviderWithSSO>{children}</AuthProviderWithSSO>;
  } else {
    return <AuthProviderWithoutSSO>{children}</AuthProviderWithoutSSO>;
  }
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Hook to check permissions directly
 * Single source of truth - based on backend permissions only
 *
 * Replaces the old useFeatures() hook with permission-based checks
 */
export const usePermissions = () => {
  const { userPermissions } = useAuth();
  const permissions = userPermissions?.permissions ?? [];

  // ==========================================
  // CORE PERMISSION CHECKS
  // ==========================================

  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  const hasAnyPermission = (...perms: string[]): boolean => {
    return perms.some(p => permissions.includes(p));
  };

  const hasAllPermissions = (...perms: string[]): boolean => {
    return perms.every(p => permissions.includes(p));
  };

  // ==========================================
  // NAVIGATION - Based on module permissions
  // ==========================================

  const canNavigateTo = (page: string): boolean => {
    // Dev mode (SSO disabled) = full admin access; skip permission checks entirely.
    if (!featureFlags.ssoEnabled) return true;

    const navigationRules: Record<string, () => boolean> = {
      'dashboard': () => permissions.length > 0,
      // TODO(revamp): organization accessible to any authenticated user with permissions (US #4260814).
      // AuthContext.tsx keeps its original name — shared infrastructure, not subject to the V2 suffix rule.
      'organization': () => permissions.length > 0,
      'people': () => hasAnyPermission(
        'CapacityManagement.People.Read',
        'CapacityManagement.People.ReadAll',
        'CapacityManagement.People.ReadSelf'
      ),
      'squad': () => hasPermission('CapacityManagement.Team.Read'),
      'tribe': () => hasPermission('CapacityManagement.ValueStream.Read'),
      'valueStream': () => hasPermission('CapacityManagement.ValueStream.Read'), // Alias for tribe
      'product': () => hasPermission('CapacityManagement.Product.Read'),
      'structure': () => hasPermission('CapacityManagement.Structure.Read'),
      'vertical': () => hasPermission('CapacityManagement.Vertical.Read'),
      'capacity': () => hasPermission('CapacityManagement.CapacityManagement.Read'),
      'scheduler': () => hasPermission('CapacityManagement.Schedule.Read'),
      'position': () => hasAnyPermission(
        'CapacityManagement.Position.Read',
        'CapacityManagement.PositionAll.Read'
      ),
      'permissions': () => hasAnyPermission(
        'CapacityManagement.FullAdmin.Execute',
        'CapacityManagement.Permissions.Manage'
      ),
      'admin': () => hasPermission('CapacityManagement.FullAdmin.Execute'),
    };

    return navigationRules[page]?.() ?? false;
  };

  // ==========================================
  // COLUMNS - Based on specific permissions
  // ==========================================

  const canSeeColumn = (columnName: string): boolean => {
    const columnRules: Record<string, () => boolean> = {
      'company': () => hasPermission('CapacityManagement.People.CompanyColumnGridRead'),
      // Add more as needed
    };

    // Default: if no specific permission, show column (conservative approach)
    return columnRules[columnName]?.() ?? true;
  };

  // ==========================================
  // FILTERS - Based on specific permissions
  // ==========================================

  const canSeeFilter = (filterName: string): boolean => {
    const filterRules: Record<string, () => boolean> = {
      'company': () => hasPermission('CapacityManagement.People.CompanyFilterRead'),
      // Add more as needed
    };

    // Default: if no specific permission, show filter (conservative approach)
    return filterRules[filterName]?.() ?? true;
  };

  // ==========================================
  // FIELDS - Based on write/read permissions
  // ==========================================

  const getFieldAccess = (fieldName: string): 'read' | 'write' | 'none' => {
    const fieldRules: Record<string, () => 'read' | 'write' | 'none'> = {
      'abiEmail': () => {
        if (hasPermission('CapacityManagement.People.WriteAbiEmail')) return 'write';
        if (hasAnyPermission('CapacityManagement.People.ReadAll', 'CapacityManagement.People.ReadSelf')) return 'read';
        return 'none';
      },
      'isLineManager': () => {
        if (hasPermission('CapacityManagement.People.WriteIsLineManager')) return 'write';
        if (hasAnyPermission('CapacityManagement.People.ReadAll', 'CapacityManagement.People.ReadSelf')) return 'read';
        return 'none';
      },
      'company': () => {
        if (hasPermission('CapacityManagement.EditPersonCompany.Write')) return 'write';
        if (hasAnyPermission('CapacityManagement.People.ReadAll', 'CapacityManagement.People.ReadSelf')) return 'read';
        return 'none';
      },
      'name': () => {
        if (hasPermission('CapacityManagement.EditPersonName.Write')) return 'write';
        if (hasAnyPermission('CapacityManagement.People.ReadAll', 'CapacityManagement.People.ReadSelf')) return 'read';
        return 'none';
      },
      'employeeId': () => {
        if (hasPermission('CapacityManagement.EditPersonID.Write')) return 'write';
        if (hasAnyPermission('CapacityManagement.People.ReadAll', 'CapacityManagement.People.ReadSelf')) return 'read';
        return 'none';
      },
      'fundedBy': () => {
        if (hasPermission('CapacityManagement.PeopleFundedBy.Write')) return 'write';
        if (hasPermission('CapacityManagement.PeopleFundedBy.Read')) return 'read';
        return 'none';
      },
      'seniority': () => {
        if (hasPermission('CapacityManagement.PeopleSeniority.Write')) return 'write';
        if (hasPermission('CapacityManagement.PeopleSeniority.Read')) return 'read';
        return 'none';
      },
      'location': () => {
        if (hasPermission('CapacityManagement.PeopleLocation.Write')) return 'write';
        if (hasPermission('CapacityManagement.PeopleLocation.Read')) return 'read';
        return 'none';
      },
      'investment': () => {
        if (hasPermission('CapacityManagement.PeopleInvestment.Write')) return 'write';
        if (hasPermission('CapacityManagement.PeopleInvestment.Read')) return 'read';
        return 'none';
      },
    };

    // Default: if no specific permission, allow read (conservative)
    return fieldRules[fieldName]?.() ?? 'read';
  };

  return {
    // Core
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Feature-based
    canNavigateTo,
    canSeeColumn,
    canSeeFilter,
    getFieldAccess,

    // Permission constants (for autocomplete)
    PERMISSIONS,
  };
};

// Re-export PERMISSIONS for direct import
export { PERMISSIONS };
